// Dashboard.js - unified Sales/Purchase/Order overview, permission-driven

import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Divider,
  Chip,
  Skeleton,
  Fade,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  alpha,
  Stack,
} from "@mui/material";

import {
  DashboardRounded,
  ReceiptLong,
  Inventory2,
  LocalShipping,
  CheckCircleRounded,
  PendingActionsRounded,
  AutorenewRounded,
  CancelRounded,
  ShowChart,
  DonutLarge,
  Timeline as TimelineIcon,
  ArrowForwardIos,
  TrendingUpRounded,
} from "@mui/icons-material";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

import {
  salesAPI,
  purchaseAPI,
  orderAPI,
  auditAPI,
} from "../services/api";

import { useAuth } from "../context/AuthContext";
import {
  STATUS_BUCKETS,
  bucketFor,
  MODULE_META,
} from "../utils/statusBuckets";

const prettyDate = (date) => {
  if (!date) return "-";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) return "-";

  return parsedDate.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const prettyChartDate = (date) => {
  if (!date) return "";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) return date;

  return parsedDate.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
  });
};

// -----------------------------------------------------------------------------
// API MAP
// -----------------------------------------------------------------------------

const API_MAP = {
  sales_tracker: salesAPI,
  purchase_tracker: purchaseAPI,
  order_tracker: orderAPI,
};

// -----------------------------------------------------------------------------
// ICON MAPS
// -----------------------------------------------------------------------------

const MODULE_ICONS = {
  sales_tracker: <ReceiptLong />,
  purchase_tracker: <Inventory2 />,
  order_tracker: <LocalShipping />,
};

const BUCKET_ICONS = {
  good: <CheckCircleRounded />,
  warning: <PendingActionsRounded />,
  serious: <AutorenewRounded />,
  critical: <CancelRounded />,
};

// -----------------------------------------------------------------------------
// BRAND
// -----------------------------------------------------------------------------

const brandColors = {
  primary: "#005B82",
  secondary: "#2586BC",
  text: "#1F2937",
  muted: "#6B7280",
  border: "#E5E7EB",
  background: "#F7F9FC",
};

// -----------------------------------------------------------------------------
// COMMON STYLES
// -----------------------------------------------------------------------------

const chartCardSx = {
  height: "100%",
  borderRadius: 2,
  backgroundColor: "#FFFFFF",
  border: `1px solid ${brandColors.border}`,
  boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
  transition: "box-shadow 0.2s ease, border-color 0.2s ease",

  "&:hover": {
    borderColor: alpha(brandColors.primary, 0.2),
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
  },
};

const sectionTitleSx = {
  fontSize: { xs: 16, md: 18 },
  fontWeight: 700,
  color: brandColors.text,
};

const sectionIconSx = {
  fontSize: 21,
  color: brandColors.primary,
};

// -----------------------------------------------------------------------------
// STAT CARD
// -----------------------------------------------------------------------------

const StatCard = ({
  title,
  value,
  icon,
  color = brandColors.primary,
  loading = false,
}) => (
  <Card
    sx={{
      height: "100%",
      borderRadius: 2,
      backgroundColor: "#FFFFFF",
      border: `1px solid ${brandColors.border}`,
      boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
      transition: "all 0.2s ease",

      "&:hover": {
        transform: "translateY(-2px)",
        borderColor: alpha(color, 0.35),
        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)",
      },
    }}
  >
    <CardContent sx={{ p: 2.5 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              color: brandColors.muted,
              fontWeight: 600,
              fontSize: 13,
              mb: 1,
            }}
          >
            {title}
          </Typography>

          {loading ? (
            <Skeleton width={90} height={42} />
          ) : (
            <Typography
              sx={{
                color: brandColors.text,
                fontSize: { xs: 28, md: 32 },
                fontWeight: 700,
                lineHeight: 1.1,
              }}
            >
              {value ?? 0}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            color,
            backgroundColor: alpha(color, 0.1),

            "& svg": {
              fontSize: 25,
            },
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// -----------------------------------------------------------------------------
// SECTION HEADER
// -----------------------------------------------------------------------------

const SectionHeader = ({ icon, title, subtitle }) => (
  <Box sx={{ mb: 2.5 }}>
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ mb: subtitle ? 0.5 : 0 }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>{icon}</Box>

      <Typography sx={sectionTitleSx}>{title}</Typography>
    </Stack>

    {subtitle && (
      <Typography
        variant="body2"
        sx={{
          color: brandColors.muted,
          fontSize: 12,
          mt: 0.5,
        }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

// -----------------------------------------------------------------------------
// DASHBOARD
// -----------------------------------------------------------------------------

const Dashboard = () => {
  const { user, canReadModule } = useAuth();

  const [loading, setLoading] = useState(true);
  const [moduleData, setModuleData] = useState({});
  const [auditLogs, setAuditLogs] = useState([]);

  // ---------------------------------------------------------------------------
  // ACCESSIBLE MODULES
  // ---------------------------------------------------------------------------

  const modules = useMemo(
    () =>
      ["sales_tracker", "purchase_tracker", "order_tracker"].filter(
        canReadModule,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user],
  );

  const modulesKey = modules.join(",");

  // ---------------------------------------------------------------------------
  // LOAD DATA
  // ---------------------------------------------------------------------------

  useEffect(() => {
    let mounted = true;

    const loadDashboard = async () => {
      setLoading(true);

      try {
        const results = await Promise.all(
          modules.map((module) =>
            Promise.all([
              API_MAP[module].getStats(),
              API_MAP[module].getRecent(),
            ]),
          ),
        );

        if (!mounted) return;

        const nextData = {};

        modules.forEach((module, index) => {
          nextData[module] = {
            stats: results[index][0].data || {},
            recent: results[index][1].data || [],
          };
        });

        setModuleData(nextData);

        // Superadmin audit logs
        if (user?.is_superuser) {
          try {
            const auditResponse = await auditAPI.getLogs();

            if (mounted) {
              setAuditLogs(auditResponse.data || []);
            }
          } catch (error) {
            console.error("Audit log load error", error);
          }
        }
      } catch (error) {
        console.error("Dashboard load error", error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      mounted = false;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modulesKey, user?.is_superuser]);

  // ---------------------------------------------------------------------------
  // STATUS TOTALS
  // ---------------------------------------------------------------------------

  const { bucketTotals, moduleBucketMatrix } = useMemo(() => {
    const totals = {
      good: 0,
      warning: 0,
      serious: 0,
      critical: 0,
    };

    const matrix = {};

    modules.forEach((module) => {
      const stats = moduleData[module]?.stats || {};

      const row = {
        good: 0,
        warning: 0,
        serious: 0,
        critical: 0,
      };

      Object.entries(stats).forEach(([key, value]) => {
        if (key === "total") return;

        const bucket = bucketFor(key);

        const numericValue = Number(value) || 0;

        if (Object.prototype.hasOwnProperty.call(row, bucket)) {
          row[bucket] += numericValue;
          totals[bucket] += numericValue;
        }
      });

      matrix[module] = row;
    });

    return {
      bucketTotals: totals,
      moduleBucketMatrix: matrix,
    };
  }, [modules, moduleData]);

  // ---------------------------------------------------------------------------
  // TREND DATA
  // ---------------------------------------------------------------------------

  const trendData = useMemo(() => {
    const dayMap = new Map();

    modules.forEach((module) => {
      (moduleData[module]?.recent || []).forEach((record) => {
        const date = new Date(record.created_at);

        if (Number.isNaN(date.getTime())) return;

        const day = date.toISOString().slice(0, 10);

        if (!dayMap.has(day)) {
          dayMap.set(day, {});
        }

        const entry = dayMap.get(day);

        entry[module] = (entry[module] || 0) + 1;
      });
    });

    return Array.from(dayMap.entries())
      .sort(([first], [second]) => (first > second ? 1 : -1))
      .map(([day, counts]) => {
        const row = {
          day,
        };

        modules.forEach((module) => {
          row[module] = counts[module] || 0;
        });

        return row;
      });
  }, [modules, moduleData]);

  // ---------------------------------------------------------------------------
  // PIE DATA
  // ---------------------------------------------------------------------------

  const pieData = useMemo(() => {
    const parts = Object.entries(bucketTotals).map(([key, value]) => ({
      name: STATUS_BUCKETS[key].label,
      value,
      color: STATUS_BUCKETS[key].color,
    }));

    const total = parts.reduce(
      (sum, item) => sum + (Number(item.value) || 0),
      0,
    );

    if (total === 0) {
      return [
        {
          name: "No Data",
          value: 1,
          color: "#CBD5E1",
        },
      ];
    }

    return parts.filter((item) => item.value > 0);
  }, [bucketTotals]);

  // ---------------------------------------------------------------------------
  // MERGED RECENT ACTIVITY
  // ---------------------------------------------------------------------------

  const mergedRecent = useMemo(() => {
    const rows = [];

    modules.forEach((module) => {
      (moduleData[module]?.recent || []).forEach((record) => {
        rows.push({
          ...record,
          _module: module,
        });
      });
    });

    return rows
      .filter((record) => record.created_at)
      .sort(
        (first, second) =>
          new Date(second.created_at) - new Date(first.created_at),
      )
      .slice(0, 10);
  }, [modules, moduleData]);

  // ---------------------------------------------------------------------------
  // TOTAL RECORDS
  // ---------------------------------------------------------------------------

  const totalRecords = useMemo(() => {
    return modules.reduce(
      (total, module) =>
        total + (Number(moduleData[module]?.stats?.total) || 0),
      0,
    );
  }, [modules, moduleData]);

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <Box
      sx={{
        minHeight: "100%",
        backgroundColor: brandColors.background,
      }}
    >
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
        <Fade in timeout={400}>
          <Box>
            {/* ================================================================ */}
            {/* PAGE HEADER */}
            {/* ================================================================ */}

            <Box
              sx={{
                mb: 3.5,
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", md: "center" },
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
              }}
            >
              <Box>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 0.75 }}
                >
                  <DashboardRounded
                    sx={{
                      fontSize: 27,
                      color: brandColors.primary,
                    }}
                  />

                  <Typography
                    sx={{
                      fontSize: { xs: 24, md: 28 },
                      fontWeight: 700,
                      color: brandColors.text,
                    }}
                  >
                    Dashboard
                  </Typography>
                </Stack>

                <Typography
                  variant="body2"
                  sx={{
                    color: brandColors.muted,
                    fontSize: { xs: 13, md: 14 },
                  }}
                >
                  Overview of sales, purchase and order activity
                </Typography>
              </Box>

              <Box
                sx={{
                  px: 2,
                  py: 1.25,
                  borderRadius: 2,
                  backgroundColor: "#FFFFFF",
                  border: `1px solid ${brandColors.border}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: brandColors.muted,
                    fontSize: 12,
                  }}
                >
                  Welcome back
                </Typography>

                <Typography
                  sx={{
                    color: brandColors.primary,
                    fontWeight: 700,
                    fontSize: 14,
                  }}
                >
                  {user?.first_name} {user?.last_name}
                </Typography>
              </Box>
            </Box>

            {/* ================================================================ */}
            {/* MODULE TOTALS */}
            {/* ================================================================ */}

            <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
              {modules.map((module) => (
                <Grid item xs={12} sm={6} md={4} key={module}>
                  <StatCard
                    title={`Total ${MODULE_META[module].label}`}
                    value={moduleData[module]?.stats?.total}
                    icon={MODULE_ICONS[module]}
                    color={MODULE_META[module].color}
                    loading={loading}
                  />
                </Grid>
              ))}

              {modules.length === 0 && !loading && (
                <Grid item xs={12}>
                  <Card sx={chartCardSx}>
                    <CardContent sx={{ p: 4, textAlign: "center" }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: brandColors.text,
                          mb: 0.75,
                        }}
                      >
                        No module access available
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ color: brandColors.muted }}
                      >
                        You don't have read access to any tracker module yet.
                        Please contact your administrator to request access.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>

            {/* ================================================================ */}
            {/* STATUS SUMMARY */}
            {/* ================================================================ */}

            {modules.length > 0 && (
              <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    title="Total Records"
                    value={totalRecords}
                    icon={<TrendingUpRounded />}
                    color={brandColors.primary}
                    loading={loading}
                  />
                </Grid>

                {Object.entries(STATUS_BUCKETS).map(([key, bucket]) => (
                  <Grid item xs={12} sm={6} md={3} key={key}>
                    <StatCard
                      title={bucket.label}
                      value={bucketTotals[key]}
                      icon={BUCKET_ICONS[key]}
                      color={bucket.color}
                      loading={loading}
                    />
                  </Grid>
                ))}
              </Grid>
            )}

            {/* ================================================================ */}
            {/* CHARTS */}
            {/* ================================================================ */}

            {modules.length > 0 && (
              <Grid container spacing={2.5}>
                {/* ------------------------------------------------------------ */}
                {/* TREND */}
                {/* ------------------------------------------------------------ */}

                <Grid item xs={12} lg={8}>
                  <Card sx={chartCardSx}>
                    <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                      <SectionHeader
                        icon={<ShowChart sx={sectionIconSx} />}
                        title="Recent Activity Trend"
                        subtitle="Record creation activity based on available recent records"
                      />

                      <Box sx={{ height: { xs: 280, md: 330 } }}>
                        {loading ? (
                          <Skeleton
                            variant="rectangular"
                            height="100%"
                            sx={{ borderRadius: 2 }}
                          />
                        ) : trendData.length === 0 ? (
                          <Box
                            sx={{
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: brandColors.muted,
                            }}
                          >
                            <Typography variant="body2">
                              No recent activity available
                            </Typography>
                          </Box>
                        ) : (
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                              data={trendData}
                              margin={{
                                top: 10,
                                right: 20,
                                left: -15,
                                bottom: 5,
                              }}
                            >
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#E5E7EB"
                                vertical={false}
                              />

                              <XAxis
                                dataKey="day"
                                tickFormatter={prettyChartDate}
                                tick={{
                                  fontSize: 11,
                                  fill: brandColors.muted,
                                }}
                                axisLine={false}
                                tickLine={false}
                              />

                              <YAxis
                                allowDecimals={false}
                                tick={{
                                  fontSize: 11,
                                  fill: brandColors.muted,
                                }}
                                axisLine={false}
                                tickLine={false}
                              />

                              <RTooltip
                                labelFormatter={(label) =>
                                  prettyChartDate(label)
                                }
                                contentStyle={{
                                  borderRadius: 8,
                                  border: "1px solid #E5E7EB",
                                  boxShadow:
                                    "0 8px 24px rgba(15,23,42,0.12)",
                                  fontSize: 12,
                                }}
                              />

                              <Legend
                                wrapperStyle={{
                                  fontSize: 12,
                                  paddingTop: 10,
                                }}
                              />

                              {modules.map((module) => (
                                <Line
                                  key={module}
                                  type="monotone"
                                  dataKey={module}
                                  name={MODULE_META[module].label}
                                  stroke={MODULE_META[module].color}
                                  strokeWidth={2.5}
                                  dot={false}
                                  activeDot={{
                                    r: 5,
                                  }}
                                />
                              ))}
                            </LineChart>
                          </ResponsiveContainer>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* ------------------------------------------------------------ */}
                {/* STATUS DISTRIBUTION */}
                {/* ------------------------------------------------------------ */}

                <Grid item xs={12} lg={4}>
                  <Card sx={chartCardSx}>
                    <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                      <SectionHeader
                        icon={<DonutLarge sx={sectionIconSx} />}
                        title="Status Distribution"
                        subtitle="Overall status across accessible modules"
                      />

                      <Box sx={{ height: { xs: 290, md: 310 } }}>
                        {loading ? (
                          <Box
                            sx={{
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Skeleton
                              variant="circular"
                              width={180}
                              height={180}
                            />
                          </Box>
                        ) : (
                          <ResponsiveContainer width="100%" height="100%">
                            <RechartsPieChart>
                              <Pie
                                data={pieData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="45%"
                                innerRadius={55}
                                outerRadius={85}
                                paddingAngle={2}
                              >
                                {pieData.map((entry, index) => (
                                  <Cell
                                    key={`status-cell-${index}`}
                                    fill={entry.color}
                                  />
                                ))}
                              </Pie>

                              <Legend
                                verticalAlign="bottom"
                                wrapperStyle={{
                                  fontSize: 12,
                                  paddingTop: 10,
                                }}
                              />

                              <RTooltip
                                formatter={(value, name) => [value, name]}
                                contentStyle={{
                                  borderRadius: 8,
                                  border: "1px solid #E5E7EB",
                                  boxShadow:
                                    "0 8px 24px rgba(15,23,42,0.12)",
                                  fontSize: 12,
                                }}
                              />
                            </RechartsPieChart>
                          </ResponsiveContainer>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* ============================================================ */}
                {/* STATUS BREAKDOWN TABLE */}
                {/* ============================================================ */}

                <Grid item xs={12}>
                  <Card sx={chartCardSx}>
                    <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                      <SectionHeader
                        icon={<DonutLarge sx={sectionIconSx} />}
                        title="Module Status Breakdown"
                        subtitle="Status count by tracker module"
                      />

                      {loading ? (
                        <Box sx={{ display: "grid", gap: 1 }}>
                          {Array.from({ length: 4 }).map((_, index) => (
                            <Skeleton key={index} height={45} />
                          ))}
                        </Box>
                      ) : (
                        <Box sx={{ overflowX: "auto" }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow
                                sx={{
                                  backgroundColor: "#F8FAFC",
                                }}
                              >
                                <TableCell
                                  sx={{
                                    fontWeight: 700,
                                    color: brandColors.text,
                                  }}
                                >
                                  Module
                                </TableCell>

                                {Object.entries(STATUS_BUCKETS).map(
                                  ([key, bucket]) => (
                                    <TableCell
                                      key={key}
                                      align="center"
                                      sx={{
                                        fontWeight: 700,
                                        color: bucket.color,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {bucket.label}
                                    </TableCell>
                                  ),
                                )}
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {modules.map((module) => (
                                <TableRow
                                  key={module}
                                  hover
                                  sx={{
                                    "&:last-child td": {
                                      borderBottom: 0,
                                    },
                                  }}
                                >
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={MODULE_META[module].label}
                                      sx={{
                                        backgroundColor: alpha(
                                          MODULE_META[module].color,
                                          0.1,
                                        ),
                                        color: MODULE_META[module].color,
                                        fontWeight: 600,
                                      }}
                                    />
                                  </TableCell>

                                  {Object.keys(STATUS_BUCKETS).map((key) => (
                                    <TableCell key={key} align="center">
                                      {moduleBucketMatrix[module]?.[key] ?? 0}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* ============================================================ */}
                {/* RECENT ACTIVITY */}
                {/* ============================================================ */}

                <Grid item xs={12}>
                  <Card sx={chartCardSx}>
                    <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: { xs: "flex-start", sm: "center" },
                          flexDirection: { xs: "column", sm: "row" },
                          gap: 2,
                          mb: 2.5,
                        }}
                      >
                        <Box>
                          <SectionHeader
                            icon={<ReceiptLong sx={sectionIconSx} />}
                            title="Recent Activity"
                            subtitle="Latest records across all accessible modules"
                          />
                        </Box>

                        {modules.includes("sales_tracker") && (
                          <Button
                            variant="contained"
                            size="small"
                            endIcon={<ArrowForwardIos />}
                            onClick={() => {
                              window.location.href = "/sales";
                            }}
                            sx={{
                              borderRadius: 1.5,
                              px: 2,
                              textTransform: "none",
                              fontWeight: 600,
                              backgroundColor: brandColors.primary,

                              "&:hover": {
                                backgroundColor: "#004A6B",
                              },
                            }}
                          >
                            View Sales
                          </Button>
                        )}
                      </Box>

                      {loading ? (
                        <Box sx={{ display: "grid", gap: 1.5 }}>
                          {Array.from({ length: 5 }).map((_, index) => (
                            <Box key={index}>
                              <Skeleton height={24} width="45%" />
                              <Skeleton height={18} width="70%" />
                              <Divider sx={{ mt: 1 }} />
                            </Box>
                          ))}
                        </Box>
                      ) : mergedRecent.length === 0 ? (
                        <Box sx={{ py: 3, textAlign: "center" }}>
                          <Typography
                            variant="body2"
                            sx={{ color: brandColors.muted }}
                          >
                            No recent records available.
                          </Typography>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            border: `1px solid ${brandColors.border}`,
                            borderRadius: 2,
                            overflow: "hidden",
                          }}
                        >
                          {mergedRecent.map((record, index) => {
                            const meta = MODULE_META[record._module];

                            const bucket = bucketFor(
                              record[meta.statusField],
                            );

                            const bucketMeta = STATUS_BUCKETS[bucket];

                            return (
                              <React.Fragment
                                key={`${record._module}-${record.id}`}
                              >
                                <Box
                                  sx={{
                                    px: { xs: 1.5, md: 2 },
                                    py: 1.5,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: 1.5,

                                    "&:hover": {
                                      backgroundColor: "#F8FAFC",
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      minWidth: 0,
                                      flex: 1,
                                    }}
                                  >
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                      flexWrap="wrap"
                                      sx={{ mb: 0.5 }}
                                    >
                                      <Chip
                                        size="small"
                                        label={meta.label}
                                        sx={{
                                          backgroundColor: alpha(
                                            meta.color,
                                            0.1,
                                          ),
                                          color: meta.color,
                                          fontWeight: 600,
                                          height: 24,
                                        }}
                                      />

                                      <Typography
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: 14,
                                          color: brandColors.text,
                                        }}
                                      >
                                        {record.sr_no || "-"} —{" "}
                                        {record.vessel_name || "-"}
                                      </Typography>
                                    </Stack>

                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: brandColors.muted,
                                        fontSize: 12,
                                      }}
                                    >
                                      Created by{" "}
                                      {record.created_by_name || "Unknown"} •{" "}
                                      {prettyDate(record.created_at)}
                                    </Typography>
                                  </Box>

                                  <Chip
                                    label={bucketMeta.label}
                                    size="small"
                                    sx={{
                                      backgroundColor: alpha(
                                        bucketMeta.color,
                                        0.12,
                                      ),
                                      color: bucketMeta.color,
                                      fontWeight: 600,
                                    }}
                                  />
                                </Box>

                                {index < mergedRecent.length - 1 && (
                                  <Divider />
                                )}
                              </React.Fragment>
                            );
                          })}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* ============================================================ */}
                {/* SYSTEM ACTIVITY LOG */}
                {/* ============================================================ */}

                {user?.is_superuser && (
                  <Grid item xs={12}>
                    <Card sx={chartCardSx}>
                      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                        <SectionHeader
                          icon={<TimelineIcon sx={sectionIconSx} />}
                          title="System Activity Log"
                          subtitle="Latest system changes and user activity"
                        />

                        {loading ? (
                          <Box sx={{ display: "grid", gap: 1.5 }}>
                            {Array.from({ length: 5 }).map((_, index) => (
                              <Box key={index}>
                                <Skeleton height={24} width="50%" />
                                <Skeleton height={18} width="70%" />
                                <Divider sx={{ mt: 1 }} />
                              </Box>
                            ))}
                          </Box>
                        ) : auditLogs.length === 0 ? (
                          <Box sx={{ py: 3, textAlign: "center" }}>
                            <Typography
                              variant="body2"
                              sx={{ color: brandColors.muted }}
                            >
                              No system activity available.
                            </Typography>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              border: `1px solid ${brandColors.border}`,
                              borderRadius: 2,
                              overflow: "hidden",
                            }}
                          >
                            {auditLogs.slice(0, 10).map((log, index) => (
                              <React.Fragment key={log.id}>
                                <Box
                                  sx={{
                                    px: { xs: 1.5, md: 2 },
                                    py: 1.75,

                                    "&:hover": {
                                      backgroundColor: "#F8FAFC",
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                      flexWrap: "wrap",
                                      gap: 1.5,
                                    }}
                                  >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography
                                        sx={{
                                          fontSize: 14,
                                          fontWeight: 600,
                                          color: brandColors.text,
                                          mb: 1,
                                        }}
                                      >
                                        {log.changed_by_name || "User"} changed{" "}
                                        <Box
                                          component="span"
                                          sx={{
                                            color: brandColors.primary,
                                          }}
                                        >
                                          {log.field_name}
                                        </Box>
                                      </Typography>

                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        flexWrap="wrap"
                                        useFlexGap
                                        sx={{ mb: 1 }}
                                      >
                                        <Chip
                                          size="small"
                                          variant="outlined"
                                          label={`Old: ${
                                            log.old_value || "—"
                                          }`}
                                          sx={{
                                            borderColor: alpha(
                                              brandColors.primary,
                                              0.25,
                                            ),
                                            fontSize: 11,
                                          }}
                                        />

                                        <Chip
                                          size="small"
                                          label={`New: ${
                                            log.new_value || "—"
                                          }`}
                                          sx={{
                                            backgroundColor: alpha(
                                              brandColors.primary,
                                              0.1,
                                            ),
                                            color: brandColors.primary,
                                            fontSize: 11,
                                          }}
                                        />
                                      </Stack>

                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: brandColors.muted,
                                        }}
                                      >
                                        {log.timestamp
                                          ? new Date(
                                              log.timestamp,
                                            ).toLocaleString()
                                          : "-"}
                                        {log.ip_address
                                          ? ` • ${log.ip_address}`
                                          : ""}
                                        {log.screen
                                          ? ` • ${log.screen}`
                                          : ""}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>

                                {index <
                                  auditLogs.slice(0, 10).length - 1 && (
                                  <Divider />
                                )}
                              </React.Fragment>
                            ))}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            )}
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default Dashboard;